import type { Chart as G2Chart } from '@tencent/chart/lib/antv/g2';
import {
    SpecialGeometryType,
    IChartElement,
    IChartElementGroup,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import {
    G2BBox,
    Matrix,
    Point,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements/types';
import _get from 'lodash/get';

export abstract class BaseChartElement {
    protected convertRectToPath(rect: IChartElement['rect'], shapeType?: string): Array<Array<string|number>> {
        const { left, top, height = 0, width = 0 } = rect;

        if (isLineShape(shapeType)) {
            // 在是线的情况下，这里以宽高作为 判断横竖线条的条件
            const isHorizontal = width >= height;
            if (isHorizontal) {
                return [
                    ['M', left, top + (height / 2)],
                    ['L', left + width, top + (height / 2)],
                ];
            }

            return [
                ['M', left + (width / 2), top],
                ['L', left + (width / 2), top + height],
            ];
        }

        const path = [
            ['M', left, top],
            ['L', left + width, top],
            ['L', left + width, top + height],
            ['L', left, top + height],
            ['Z'],
        ];

        return path;
    }

    protected convertBBoxToRect(bbox: G2BBox, matrix?: Matrix): IChartElement['rect'] {
        const { minX, minY, maxX, maxY } = bbox;

        if (matrix) {
            const minPoint = this.applyMatrix({ x: minX, y: minY }, matrix);
            const maxPoint = this.applyMatrix({ x: maxX, y: maxY }, matrix);

            return {
                left: minPoint.x,
                top: minPoint.y,
                height: maxPoint.y - minPoint.y,
                width: maxPoint.x - minPoint.x,
            };
        }

        return {
            height: maxY - minY,
            width: maxX - minX,
            left: minX,
            top: minY,
        };
    }

    // g2里的matrix是这个类型
    // ┌  a  c  0 ┐
    // │  b  d  0 │
    // └ tx ty  1 ┘
    protected applyMatrix(point: Point, matrix: Matrix): Point {
        const { x, y } = point;
        const [a, c, , b, d, , tx, ty] = matrix;

        return {
            x: (a * x) + (c * y) + tx,
            y: (b * x) + (d * y) + ty,
        };
    }

    protected groupBy<T>(elements: T[], path: string): Record<string, T[]> {
        return elements.reduce((result, cur) => {
            const key = _get(cur, path);
            if (result[key]) {
                result[key].push(cur);
            } else {
                result[key] = [cur];
            }
            return result;
        }, {});
    }

    protected isAreaShape(shapeType?: string) {
        return shapeType === 'area';
    }

    protected isMarkerShape(shapeType?: string) {
        return shapeType === 'circle';
    }

    public abstract getElements(chart: G2Chart): Array<IChartElement | IChartElementGroup>;
}

export function isLineShape(shapeType?: string) {
    return shapeType === 'line' || shapeType === 'smooth' || shapeType === 'stick';
}

export function isTrendLine(specialGeometryType) {
    return specialGeometryType === SpecialGeometryType.trendLine;
}

export function isUpDownBars(specialGeometryType) {
    return specialGeometryType === SpecialGeometryType.upDownBar;
}

export function isHighLowLine(specialGeometryType) {
    return specialGeometryType === SpecialGeometryType.highLowLine;
}

